/** Bridge between the widget's committed-chip representation and the tree.
 *
 * A `ChipToken` is one atomic, committed unit in the input row (a whole
 * condition, a bare word, or a structural token). Rather than duplicating parse
 * logic, `chipsToTree` re-serialises the chip list into query-string syntax and
 * runs it through the well-tested `parse()`; `treeToChips` is the inverse via
 * `tokenize(serialize(tree))`.
 */
import { parse } from './parser';
import { quote, serialize } from './serializer';
import { tokenize, type Token } from './tokenizer';
import type { FilterNode, FilterValue, NamespaceConfigMap } from './types';

export type ChipToken =
  | { t: 'cond'; namespace: string; operator: string; value: FilterValue; scope?: 'all' }
  | { t: 'word'; text: string }
  | { t: 'and' }
  | { t: 'or' }
  | { t: 'not' }
  | { t: 'lparen' }
  | { t: 'rparen' };

/** Render one chip as a query-string fragment. */
export function chipToString(chip: ChipToken): string {
  switch (chip.t) {
    case 'cond': {
      const star = chip.scope === 'all' ? '*' : '';
      if (chip.operator === 'range' && Array.isArray(chip.value)) {
        return `${chip.namespace}=${star}${quote(chip.value[0])}..${quote(chip.value[1])}`;
      }
      const scalar = Array.isArray(chip.value) ? chip.value[0] : chip.value;
      // gt/lt render as open-ended ranges: `ns=v..` / `ns=..v`.
      if (chip.operator === 'gt') return `${chip.namespace}=${star}${quote(scalar)}..`;
      if (chip.operator === 'lt') return `${chip.namespace}=${star}..${quote(scalar)}`;
      const sigil = chip.operator === 'icontains' ? '=~' : chip.operator === 'idmatch' ? '=#' : '=';
      return `${chip.namespace}${sigil}${star}${quote(scalar)}`;
    }
    case 'word':
      return quote(chip.text);
    case 'and':
      return '&';
    case 'or':
      return '|';
    case 'not':
      return '!';
    case 'lparen':
      return '(';
    case 'rparen':
      return ')';
  }
}

/** Serialise chips to query text and parse into a tree. */
export function chipsToTree(chips: ChipToken[], namespaces: NamespaceConfigMap): FilterNode[] {
  const text = chips.map(chipToString).join(' ');
  return parse(text, namespaces).nodes;
}

/** Inverse: fold serialize(tree)'s token stream back into committed chips. */
export function treeToChips(tree: FilterNode[]): ChipToken[] {
  return tokensToChips(tokenize(serialize(tree)).tokens);
}

/** Fold a token stream into committed chips, VERBATIM — no parse in between.
 *  An NSOP(+value(+RANGE+value2)) sequence folds into one cond chip; a
 *  WORD/QUOTED folds into a word chip; structural tokens map 1:1, including a
 *  leading or trailing connective and an unmatched bracket (the parser would
 *  drop those — the paste path relies on them surviving so `| tag=b` appended
 *  after `tag=a` still ORs). A cond chip's `operator` is the sigil's default
 *  (`iexact`/`icontains`/`idmatch`, `range`/`gt`/`lt`); the namespace's real
 *  operator is resolved when the chips are re-parsed by `chipsToTree`. */
export function tokensToChips(tokens: Token[]): ChipToken[] {
  const chips: ChipToken[] = [];
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i]!;
    switch (t.kind) {
      case 'NSOP': {
        const first = tokens[i + 1];
        const scope = t.all ? ('all' as const) : undefined;
        // Open-start range `ns=..v` → an `lt` chip.
        if (first?.kind === 'RANGE') {
          const bound = tokens[i + 2];
          if (bound && (bound.kind === 'WORD' || bound.kind === 'QUOTED')) {
            chips.push({ t: 'cond', namespace: t.ns, operator: 'lt', value: bound.text, ...(scope ? { scope } : {}) });
            i += 3;
            break;
          }
        }
        if (first && (first.kind === 'WORD' || first.kind === 'QUOTED')) {
          const range = tokens[i + 2];
          if (range?.kind === 'RANGE') {
            const second = tokens[i + 3];
            if (second && (second.kind === 'WORD' || second.kind === 'QUOTED')) {
              chips.push({
                t: 'cond',
                namespace: t.ns,
                operator: 'range',
                value: [first.text, second.text],
                ...(scope ? { scope } : {}),
              });
              i += 4;
              break;
            }
            // Open-end range `ns=v..` → a `gt` chip.
            chips.push({ t: 'cond', namespace: t.ns, operator: 'gt', value: first.text, ...(scope ? { scope } : {}) });
            i += 3;
            break;
          }
          const operator = t.sigil === '=~' ? 'icontains' : t.sigil === '=#' ? 'idmatch' : 'iexact';
          chips.push({ t: 'cond', namespace: t.ns, operator, value: first.text, ...(scope ? { scope } : {}) });
          i += 2;
          break;
        }
        // Malformed NSOP with no value — skip it.
        i += 1;
        break;
      }
      case 'WORD':
      case 'QUOTED':
        chips.push({ t: 'word', text: t.text });
        i += 1;
        break;
      case 'AMP':
        chips.push({ t: 'and' });
        i += 1;
        break;
      case 'PIPE':
        chips.push({ t: 'or' });
        i += 1;
        break;
      case 'BANG':
        chips.push({ t: 'not' });
        i += 1;
        break;
      case 'LPAREN':
        chips.push({ t: 'lparen' });
        i += 1;
        break;
      case 'RPAREN':
        chips.push({ t: 'rparen' });
        i += 1;
        break;
      case 'RANGE':
        // Stray range with no preceding NSOP context — skip.
        i += 1;
        break;
    }
  }
  return chips;
}
